import { Chat as ChatIcon } from "@mui/icons-material";
import type { ColumnDef } from "@tanstack/react-table";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AIChat, { type AIChatHandle } from "~/components/aiChat";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import DiffViewer, { type DiffViewerHandle } from "~/components/aiChat/diff";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import ExportModal, { type TableExportData } from "~/components/Document/exportModal";
import { MarkdownContent, markdownContentClassName } from "~/components/Document/MarkdownContent";
import MarkdownEditorPreview from "~/components/Document/MarkdownEditorPreview";
import KnowledgeIndicator from "~/components/Project/knowledge/KnowledgeIndicator";
import KnowledgeSidebar from "~/components/Project/knowledge/KnowledgeSidebar";
import Button from "~/components/ui/button";
import Modal from "~/components/ui/modal";
import { TOAST_MESSAGES } from "~/constants/messages";
import NonFunctionalRequirementTable from "~/features/nonFuncRequirement/nonFunctionalRequirementTable";
import { AIChatApi } from "~/functions/api/aiChat";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useKnowledgeCount } from "~/hooks/useKnowledgeCount";
import { useMarkdownRenderer } from "~/hooks/useMarkdownRenderer";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import DeleteIcon from "~/images/delete_row.svg?react";
import {
  acquireLock,
  addRow,
  clearError,
  deleteRow,
  fetchNonFunctionalRequirement,
  releaseLock,
  restoreFromCopyData,
  updateMarkdownContent,
  updateNonFunctionalRequirement,
  updateRow,
} from "~/slices/nonFunctionalRequirementSlice";
import { initializeSessions } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import chatStyles from "../systemProposal/styles.module.scss";
import styles from "./index.module.scss";

// 初期データ
interface RowData {
  その他機能: string;
  内訳: string;
  内容: string;
  注記: string;
}

function createRowData(その他機能: string, 内訳: string, 内容: string, 注記: string): RowData {
  const rowData: RowData = { その他機能, 内訳, 内容, 注記 };
  return rowData;
}

const NonFuncRequirement = () => {
  const { renderMarkdown } = useMarkdownRenderer();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedId } = useParams<{ selectedId: string }>();

  const { error: projectError } = useProjectMembers();
  const { canAccessChat } = useProjectPermission();

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();

  const { data, isEditable, isLoading, error, documentInfo, isMarkdown, markdownContent } =
    useSelector((state: RootState) => state.nonFunctionalRequirement);
  // const { projectMembers } = useSelector((state: RootState) => state.auth);
  const [showExportModal, setShowExportModal] = useState(false);
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  // ナレッジインジケーター用のカウント取得
  const { count: knowledgeCount, totalCount: knowledgeTotalCount } = useKnowledgeCount(
    "non-functional-requirements",
  );
  // ナレッジサイドバーの状態
  const [isKnowledgeSidebarOpen, setIsKnowledgeSidebarOpen] = useState(false);
  // AIChat関連の状態
  const { currentSession } = useSelector((state: RootState) => state.project);
  const [showAIChat, setShowAIChat] = useState(true);
  const [isDiffModalOpen, setDiffModalOpen] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [threads, setThreads] = useState<any[]>([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const diffViewerRef = React.useRef<DiffViewerHandle>(null);
  const [currentThreadId, setCurrentThreadId] = useState<string>("");
  const [documentBefore, setDocumentBefore] = useState<any>(null);
  const [documentAfter, setDocumentAfter] = useState<any>(null);
  const [isDiffSaving, setIsDiffSaving] = useState(false);
  const [diffViewerHeader, setDiffViewerHeader] = useState<React.ReactNode>(null);

  const toggleEditMode = () => {
    if (isEditable) {
      handleUpdate(); // 保存処理を実行
    } else {
      handleLock();
    }
  };

  const handleLock = async () => {
    await dispatch(acquireLock());
  };

  const handleCancel = useCallback(async () => {
    try {
      dispatch(restoreFromCopyData());
      const result = await dispatch(releaseLock());
      if (releaseLock.fulfilled.match(result)) {
        toast.success(TOAST_MESSAGES.DOCUMENT.EDIT_CANCEL_SUCCESS);
      } else if (releaseLock.rejected.match(result)) {
        toast.error(TOAST_MESSAGES.DOCUMENT.EDIT_CANCEL_FAILED);
      }
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(TOAST_MESSAGES.DOCUMENT.CANCEL_ERROR);
    }
  }, [dispatch]);

  const handleUpdate = useCallback(async (): Promise<void> => {
    await dispatch(updateNonFunctionalRequirement());
  }, [dispatch]);

  // 編集中の場合、ナビゲーションをブロック
  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    handleUpdate,
    handleCancel,
  );

  const updateData = (rowIndex: number, columnId: keyof RowData, value: string) => {
    const updatedRow = { ...data[rowIndex]!, [columnId]: value } as RowData;
    dispatch(updateRow({ index: rowIndex, data: updatedRow }));
  };

  const getExportData = (): TableExportData => {
    const name = "非機能要件書";
    if (isMarkdown) {
      return {
        headers: [],
        rows: [],
        name,
        markdownContent: markdownContent,
      };
    }
    const headers = ["その他機能", "内訳", "内容", "注記"];
    const rows = data.map((row) =>
      headers.map((header) => row[header as keyof RowData]?.toString() || ""),
    );
    return { headers, rows, name };
  };

  useEffect(() => {
    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得、なければReduxストアから取得
    const documentId = selectedId || currentProject?.outputs?.["non-functional-requirements"]?.id;

    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.NON_FUNC_REQUIREMENT_NOT_GENERATED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    dispatch(fetchNonFunctionalRequirement(documentId));
  }, [dispatch, currentProject, currentUser, navigate, selectedId]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (projectError) {
      console.error("Project data loading error:", projectError);
    }
  }, [projectError]);

  // Initialize session IDs (reinit on document change)
  const nonFuncReqDocId = currentProject?.outputs?.["non-functional-requirements"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(nonFuncReqDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, nonFuncReqDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    // スレッド作成時や手動リロード時に履歴を更新
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[NonFuncRequirement] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleApprovedDocumentChange = async (approvedDocument: string) => {
    setIsDiffSaving(true);
    try {
      await dispatch(acquireLock());
      dispatch(updateMarkdownContent(approvedDocument));
      await dispatch(updateNonFunctionalRequirement());
      await dispatch(fetchNonFunctionalRequirement());
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsDiffSaving(false);
    }
  };

  const handleDiffClose = () => {
    setIsDiffSaving(false);
    setDiffModalOpen(false);
    setShowAIChat(false);
    setDocumentBefore(null);
    setDocumentAfter(null);
    setDiffViewerHeader(null);
  };

  const handleDiffApply = () => {
    if (isDiffSaving) return;
    void diffViewerRef.current?.applyChanges();
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        // カスタムメッセージ（古いブラウザでは表示される）
        event.preventDefault();
        event.returnValue = ""; // Chrome用: 空文字を指定
      }
    };

    // beforeunload イベントの登録
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // クリーンアップ
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable]);

  // fetchDocumentInfo useEffectは削除（Reduxで統合済み）

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const columns: ColumnDef<(typeof data)[0]>[] = [
    { accessorKey: "その他機能", header: "その他機能" },
    { accessorKey: "内訳", header: "内訳" },
    { accessorKey: "内容", header: "内容" },
    { accessorKey: "注記", header: "注記" },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            onClick={() => dispatch(deleteRow(row.index))}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  const shouldUseMarkdownMode = isMarkdown;

  const addNewRow = () => {
    if (shouldUseMarkdownMode) return;
    const newRow = createRowData("", "", "", "");
    dispatch(addRow(newRow));
  };

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  return (
    <div className={styles["nonFuncRequirement"]}>
      {documentInfo && (
        <>
          <CommonDocumentHeader
            title="非機能要件書"
            documentType="non-functional-requirements"
            createdAt={documentInfo.createdAt}
            modifiedAt={documentInfo.modifiedAt}
            {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
            isEditable={isEditable}
            isLoading={isLoading}
            handleCancel={handleCancel}
            toggleEditMode={toggleEditMode}
            handleExportClick={handleExportClick}
            handleHistoryClick={handleHistoryClick}
            showEditButton={true}
            showExportButton={true}
          />
          {false && (
            <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
              <KnowledgeIndicator
                documentType="non-functional-requirements"
                activeCount={knowledgeCount}
                totalCount={knowledgeTotalCount}
              />
            </div>
          )}
        </>
      )}
      {showExportModal && (
        <ExportModal
          documentType="non-functional-requirements"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "non-functional-requirement",
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "stretch",
          position: "relative",
          minHeight: "calc(100vh - 196px)",
        }}
      >
        <div style={{ flex: "1 1 auto" }}>
          {shouldUseMarkdownMode ? (
            <div className={styles["document-content"]}>
              <div className={styles["markdown-mode-content"]}>
                {isEditable ? (
                  <MarkdownEditorPreview
                    value={markdownContent}
                    onChange={(value) => dispatch(updateMarkdownContent(value))}
                    previewContent={renderMarkdown(markdownContent || "")}
                    textareaClassName={styles["markdown-editor"] || ""}
                    previewClassName={markdownContentClassName}
                  />
                ) : (
                  <MarkdownContent>{renderMarkdown(markdownContent || "")}</MarkdownContent>
                )}
              </div>
            </div>
          ) : (
            <>
              <NonFunctionalRequirementTable
                data={data}
                columns={columns}
                updateData={updateData}
                isEditable={isEditable}
              />
              {isEditable && (
                <Button label="追加" onClick={addNewRow} styleType="add-row" icon="addRowIcon" />
              )}
            </>
          )}
        </div>
        {!showAIChat && (
          <KnowledgeSidebar
            isOpen={isKnowledgeSidebarOpen}
            onClose={() => setIsKnowledgeSidebarOpen(false)}
            documentType="non-functional-requirements"
          />
        )}
        {showAIChat && (
          <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
            <ChatHistoryPanel
              open={showChatHistory}
              placement="sidebar"
              title="履歴（最新10件）"
              loading={historyLoading}
              threads={threads}
              activeThreadId={currentThreadId}
              onClose={() => setShowChatHistory(false)}
              onRefresh={refreshThreadHistory}
              onLoadThread={async (t) => {
                const tid = t.id || t.threadId;
                if (tid) {
                  setCurrentThreadId(tid);
                }
                await aiChatRef.current?.loadThread(t, "agent");
              }}
            />
            <AIChat
              ref={aiChatRef}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
              onPreviewDiff={(before, after) => {
                setDocumentBefore(before);
                setDocumentAfter(after);
                setDiffModalOpen(before != null && after != null);
              }}
              height="calc(100vh - 196px)"
              maxHeight="none"
              currentDocumentId={currentProject?.outputs?.["non-functional-requirements"]?.id || ""}
              currentProjectId={currentProject?.id || ""}
              currentDocumentType="non-functional-requirements"
              originalDocument={markdownContent}
              threadId={currentSession?.threadId}
              resourceId={currentSession?.resourceId}
              onOpenHistory={async () => {
                setShowChatHistory(true);
                await refreshThreadHistory();
              }}
              onThreadUpdate={refreshThreadHistory}
              onThreadChange={(tid) => setCurrentThreadId(tid)}
            />
          </div>
        )}
      </div>

      {!showAIChat && !isEditable && !isKnowledgeSidebarOpen && shouldShowChatIcon && (
        <div className={chatStyles["floatingButtons"]}>
          <KnowledgeIndicator
            documentType="non-functional-requirements"
            activeCount={knowledgeCount}
            totalCount={knowledgeTotalCount}
            compact
            onOpen={() => setIsKnowledgeSidebarOpen(true)}
          />
          <button
            className={chatStyles["chatButton"]}
            onClick={() => setShowAIChat(true)}
            aria-label="AIチャットを開く"
          >
            <ChatIcon />
          </button>
        </div>
      )}

      <Modal
        label="差分プレビュー"
        isOpen={isDiffModalOpen && !!documentBefore && !!documentAfter}
        onClose={handleDiffClose}
        className="diffPreviewModal"
        usePortal
        subHeader={diffViewerHeader}
        content={
          !!documentBefore && !!documentAfter ? (
            <div className={chatStyles["diffModalContent"]}>
              <div className={chatStyles["diffModalScroll"]}>
                <DiffViewer
                  ref={diffViewerRef}
                  originalContent={
                    typeof documentBefore === "string"
                      ? documentBefore
                      : JSON.stringify(documentBefore, null, 2)
                  }
                  updatedContent={
                    typeof documentAfter === "string"
                      ? documentAfter
                      : JSON.stringify(documentAfter, null, 2)
                  }
                  onApprovedDocumentChange={handleApprovedDocumentChange}
                  onClose={handleDiffClose}
                  renderHeaderExternally
                  onHeaderChange={setDiffViewerHeader}
                />
              </div>
            </div>
          ) : null
        }
        footer={
          <div className={chatStyles["diffModalFooter"]}>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonSecondary"]}`}
              onClick={handleDiffClose}
              disabled={isDiffSaving}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonPrimary"]}`}
              onClick={handleDiffApply}
              disabled={isDiffSaving || !documentBefore || !documentAfter}
            >
              反映する
            </button>
          </div>
        }
      />
    </div>
  );
};

export default NonFuncRequirement;
